import React, { useEffect, useState } from "react";
import api from "./api";
import { FileText, FilePdf, FileImage, FileVideo, FileAudio, FileDoc, File as FileIcon, Download, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiError } from "./format";

export const ACCEPTED_TYPES = "image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip";
export const MAX_MB = 50;

export function prettySize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function iconFor(contentType = "") {
  if (contentType.startsWith("image/")) return FileImage;
  if (contentType.startsWith("video/")) return FileVideo;
  if (contentType.startsWith("audio/")) return FileAudio;
  if (contentType === "application/pdf") return FilePdf;
  if (contentType.includes("word") || contentType === "text/plain") return FileDoc;
  if (contentType.includes("sheet") || contentType === "text/csv") return FileText;
  return FileIcon;
}

export async function uploadAttachment(file) {
  if (file.size > MAX_MB * 1024 * 1024) {
    toast.error(`El archivo supera el máximo de ${MAX_MB} MB`);
    return null;
  }
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  } catch (e) {
    const status = e?.response?.status;
    const detail = String(e?.response?.data?.detail || e?.message || "");
    let msg;
    if (status === 402 || /payment required/i.test(detail) || /402/.test(detail)) {
      msg = "Almacenamiento sin saldo. Avisa al administrador para cargar el Universal Key.";
    } else if (status === 413) {
      msg = `El archivo supera el máximo permitido (${MAX_MB} MB)`;
    } else if (status === 415) {
      msg = "Tipo de archivo no permitido";
    } else if (status === 502) {
      msg = "Servicio de almacenamiento no disponible. Intenta de nuevo en unos minutos.";
    } else {
      msg = apiError(e, "No se pudo subir el archivo");
    }
    toast.error(msg);
    return null;
  }
}

/** Pending (pre-send) attachment chip */
export function PendingAttachment({ att, onRemove }) {
  const Icon = iconFor(att.content_type);
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 border border-hairline bg-surface max-w-full" data-testid="chat-pending-attachment">
      <Icon size={18} weight="duotone" className="text-forest flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{att.filename}</div>
        <div className="label-mono">{prettySize(att.size)}</div>
      </div>
      <button type="button" onClick={onRemove} className="p-1 text-inkMuted hover:text-danger" data-testid="chat-remove-attachment" aria-label="Quitar adjunto">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

/** Bubble attachment — renders inline image/video or a downloadable chip */
export function MessageAttachment({ attachment, mine }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImage = attachment.content_type?.startsWith("image/");
  const isVideo = attachment.content_type?.startsWith("video/");
  const isAudio = attachment.content_type?.startsWith("audio/");

  useEffect(() => {
    if (!isImage && !isVideo && !isAudio) return;
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${attachment.id}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(resp.data);
        setBlobUrl(url);
      } catch {}
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [attachment.id, isImage, isVideo, isAudio]);

  const download = async () => {
    try {
      const resp = await api.get(`/files/${attachment.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) { toast.error(apiError(e, "No se pudo descargar")); }
  };

  if (isImage) {
    return (
      <button onClick={download} className="block" data-testid="chat-msg-image">
        {blobUrl ? (
          <img src={blobUrl} alt={attachment.filename} className="max-w-[260px] max-h-[260px] object-cover border border-hairline" />
        ) : (
          <div className="w-[200px] h-[140px] bg-bg border border-hairline animate-pulse" />
        )}
        <div className={`label-mono mt-1 ${mine ? "text-bg/60" : "text-inkMuted"}`}>{attachment.filename}</div>
      </button>
    );
  }
  if (isVideo) {
    return (
      <div data-testid="chat-msg-video">
        {blobUrl ? (
          <video src={blobUrl} controls className="max-w-[280px] border border-hairline" />
        ) : (
          <div className="w-[260px] h-[160px] bg-bg border border-hairline animate-pulse" />
        )}
        <div className={`label-mono mt-1 ${mine ? "text-bg/60" : "text-inkMuted"}`}>{attachment.filename}</div>
      </div>
    );
  }
  if (isAudio) {
    return (
      <div data-testid="chat-msg-audio">
        {blobUrl && <audio src={blobUrl} controls className="max-w-[260px]" />}
        <div className={`label-mono mt-1 ${mine ? "text-bg/60" : "text-inkMuted"}`}>{attachment.filename}</div>
      </div>
    );
  }
  const Icon = iconFor(attachment.content_type);
  return (
    <button
      onClick={download}
      data-testid="chat-msg-file"
      className={`flex items-center gap-2 px-3 py-2 border ${mine ? "bg-forest-hover border-bg/20 text-bg" : "bg-surface border-hairline"} hover:border-burnt max-w-[260px]`}
    >
      <Icon size={22} weight="duotone" className={mine ? "text-bg" : "text-forest"} />
      <div className="min-w-0 flex-1 text-left">
        <div className="text-sm font-medium truncate">{attachment.filename}</div>
        <div className={`label-mono ${mine ? "text-bg/60" : ""}`}>{prettySize(attachment.size)}</div>
      </div>
      <Download size={14} weight="bold" />
    </button>
  );
}

/** Attach button — wraps hidden input */
export function AttachButton({ onFile, disabled, testid = "chat-attach-btn" }) {
  const ref = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const att = await uploadAttachment(file);
    setBusy(false);
    if (att) onFile(att);
  };
  return (
    <>
      <input ref={ref} type="file" accept={ACCEPTED_TYPES} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={disabled || busy}
        className="h-12 w-12 border border-hairline flex items-center justify-center hover:bg-bg disabled:opacity-50"
        data-testid={testid}
        aria-label="Adjuntar archivo"
      >
        {busy ? <div className="w-4 h-4 border-2 border-forest border-t-transparent animate-spin" /> : <Paperclip size={18} weight="bold" />}
      </button>
    </>
  );
}
